import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Divider, Flex, Typography } from 'antd';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import SidebarTemplate from '../../../sidebars/sidebarElements/SidebarTemplate';
import ColorDot from './colorFilter/ColorDot';
import { COLLECTION_SIDEBAR_IDS } from '../constants';
import { collectionHexagonsSummary } from '../utils/collectionHexagonsSummary';
import HexagonStats from './HexagonStats';

const { Text } = Typography;

/**
 * The "Hexagons" inspector card: portals into the modify-layers sidebar like
 * CollectionFilter/ObjectFilter, but aligned to the *right* of the map, mirroring
 * the model path's HexagonInfo. The side is the point — the classification editor
 * holds the left, so a right-hand card lets you inspect a cell while a
 * classification is applied instead of the two panels evicting each other.
 * Opened on demand only (the layer row's details button, or selecting a cell).
 *
 * Content is context-driven over one reused component (HexagonStats):
 *   - a cell is selected → that cell's detail, labelled with its class
 *   - nothing selected   → the whole-collection summary
 * plus the cell size (resolution), read-only — editing it lives in the
 * Hexagon Settings panel. Closing clears the selection so the highlight doesn't
 * linger.
 *
 * @param {object} props
 * @param {import('../utils/collectionHexagonsToCells').HexagonCell[]} props.cells
 * @param {Record<string, string>} props.classByCellId - per-cell class id, empty
 *   unless a classification is currently applied to the map
 * @param {object} props.results - per-class results, source of the class label/color
 */
const CollectionHexagonPanel = ({ cells, classByCellId, results }) => {
	const { modifyLayersSidebarRef } = useProject();

	const selectedCellId = useCollectionStore(s => s.selectedHexagonCellId);
	const setSelectedHexagonCellId = useCollectionStore(s => s.setSelectedHexagonCellId);
	const resolution = useCollectionStore(s => s.hexagonResolution);

	const summary = useMemo(() => collectionHexagonsSummary(cells), [cells]);
	// Re-derived from the live `cells` prop on every render (not read from the
	// store as a stored object) so a resolution/filter change that refetches
	// cells can't leave this panel showing stale stats for a cell that no
	// longer exists — see the layer's own selection-outline effect for the
	// same pattern.
	const selectedCell =
		selectedCellId != null ? (cells.find(c => c.id === selectedCellId) ?? null) : null;

	if (!modifyLayersSidebarRef?.current) return null;

	const showingCell = !!selectedCell;
	const stats = showingCell ? selectedCell.stats : summary?.stats;
	const detections = showingCell ? selectedCell.detections : summary?.detections;

	// Only set while a classification is actually applied to the map, so the card
	// names the class the user can see rather than one from a closed panel.
	const selectedClass = showingCell ? results?.[classByCellId?.[selectedCell.id]] : null;

	return createPortal(
		<SidebarTemplate
			zindex="3"
			align="right"
			sidebarId={COLLECTION_SIDEBAR_IDS.hexagonPanel}
			header="Hexagons"
			closeable
			onHide={() => setSelectedHexagonCellId(null)}>
			<Flex vertical gap={12} style={{ padding: '12px 16px' }}>
				{/* Cell size: read-only here — edited in the Hexagon Settings panel */}
				<Flex align="center" justify="space-between" gap={8}>
					<Text style={{ fontSize: '0.85rem' }}>Cell size (m²)</Text>
					<Text style={{ fontSize: '0.85rem' }}>{resolution}</Text>
				</Flex>

				<Divider style={{ margin: 0 }} />

				{/* Context label: selected cell (with its class, when the map is
				    classified) vs whole-collection summary */}
				<Text type="secondary" style={{ fontSize: '0.8rem' }}>
					{showingCell
						? 'Selected cell'
						: `All hexagons · ${summary?.cellCount ?? 0} cells`}
					{selectedClass && (
						<>
							{' · '}
							<ColorDot color={selectedClass.color} />
							{selectedClass.label}
						</>
					)}
				</Text>

				{stats ? (
					<HexagonStats stats={stats} detections={detections} />
				) : (
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						No hexagon data.
					</Text>
				)}
			</Flex>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default CollectionHexagonPanel;
